/**
 * The peon stamp: a peon drawn small in their own look (shared/weekly.ts GALLERY_PEON, the mascot in
 * their shirt with their hair, beard and extras) in a one-pixel ink frame with a pixel of paper inside
 * it, on parchment. One renderer on each client (src/components/PeonStamp.tsx,
 * client/components/PeonStamp.tsx), worn in two places:
 *
 * - a closed note slip, in place of the author's name (owner, 2026-10-05: "Rather than the peons name
 *   on the minimized notes, it should just be a little stamp of their peon"; shared/notes.ts NOTE_STAMP);
 * - a board card whose task the roulette wheel assigned, in the card's bottom-left corner, the chosen
 *   peon (owner, 2026-10-05: "When a peon is chosen via roulette, a stamp of their peon should be
 *   placed in the bottom left of the card").
 *
 * No text beside it; a screen reader hears its label.
 */
import { taskRouletteLine } from "./roulette.ts";
import type { Task } from "./types";

/** The stamp's frame: a one-pixel ink edge with a pixel of paper inside it, as on the note slip. */
export const PEON_STAMP = { edge: 1, inset: 1 } as const;

/** Where the card's stamp sits: this far in from the card's content box's bottom-left corner (over the thumb's corner). */
export const CARD_STAMP = { left: 4, bottom: 4 } as const;

/**
 * Whose stamp a board card wears: the wheel's pick while rouletteUI is on and the task is open with
 * its wheel unsettled, the very condition of the task screen's "Assigned to Hue" line (the task read
 * carries `roulette_winner` and `roulette_stake` only then, docs/roulette.md "Data"). A task claimed
 * by hand has no winner, so no stamp.
 */
export function cardRouletteWinner(task: Pick<Task, "status" | "roulette_winner" | "roulette_stake">, rouletteUI: boolean): string | null {
  return rouletteUI && task.status === "open" && task.roulette_winner && task.roulette_stake ? task.roulette_winner : null;
}

/** The card stamp's name for a screen reader: "Assigned to Hue by roulette", "Assigned to you by roulette". */
export function rouletteStampLabel(winner: string, me: string | null | undefined): string {
  return `${taskRouletteLine(winner, me, 0)} by roulette`;
}
